/**
 * Canvas 对象分批处理调度系统
 *
 * 解决画布对象数量较多时，一次性遍历所有对象阻塞主线程导致卡顿的问题。
 *
 * 架构：
 *   BatchScheduler（调度中心）── 管理所有 BatchProcessor 实例
 *     ├── BatchProcessor A (setCoords)
 *     ├── BatchProcessor B (selectable)
 *     └── BatchProcessor C (restore)
 *
 * 核心能力：
 * 1. 全局版本号：任何新操作（run/schedule/cancelAll）使所有旧分批任务失效
 * 2. 统一 rAF：多个 processor 的 schedule 合并到同一帧处理
 * 3. 共享帧时间预算：同一帧内多个 processor 不会各自占满时间
 * 4. 一键清理：画布切换/销毁时 cancelAll() 一次清空所有待处理任务
 *
 * @example
 * ```ts
 * const scheduler = new BatchScheduler();
 *
 * const coordsSync = scheduler.createProcessor({
 *   getItems: () => canvas.getObjects(),
 *   processItem: (obj) => obj.setCoords(),
 * });
 *
 * coordsSync.run();       // 立即执行（同步或分批）
 * coordsSync.schedule();  // 合并到下一帧
 * scheduler.cancelAll();  // 画布切换时一键清理
 * scheduler.destroy();    // 画布销毁时彻底清理
 * ```
 */

// ============================================================
// BatchScheduler - 全局调度中心
// ============================================================

export class BatchScheduler {
  /**
   * 全局版本号。每次 cancelAll / destroy / 新一轮 flush 时递增，
   * 所有版本号不匹配的分批任务自动中止。
   */
  private _globalVersion = 0;
  private _rafId: number | null = null;
  private _processors = new Set<BatchProcessor<any>>();

  /** 每帧总时间预算(ms)，所有 processor 共享 */
  readonly frameBudgetMs: number;

  constructor(frameBudgetMs: number = 8) {
    this.frameBudgetMs = frameBudgetMs;
  }

  /** 当前全局版本号 */
  get version(): number {
    return this._globalVersion;
  }

  /**
   * 创建一个绑定到此调度器的 BatchProcessor。
   * 创建即注册，destroy 时自动解除。
   */
  createProcessor<T>(options: BatchProcessorOptions<T>): BatchProcessor<T> {
    const processor = new BatchProcessor<T>(options, this);
    this._processors.add(processor);
    return processor;
  }

  /** 将 processor 从调度器中移除（processor.destroy 内部调用） */
  _unregister(processor: BatchProcessor<any>): void {
    this._processors.delete(processor);
  }

  /**
   * 请求在下一帧执行调度器 flush。
   * 多次调用只会产生一个 rAF。
   */
  _requestFlush(): void {
    if (this._rafId !== null) return;

    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._flush();
    });
  }

  /**
   * 统一 flush：在一帧里依次运行所有有待处理的 processor，
   * 共享 frameBudgetMs 时间预算。
   */
  private _flush(): void {
    const frameStart = performance.now();
    const version = this._globalVersion;

    for (const processor of this._processors) {
      if (version !== this._globalVersion) return;
      if (!processor._hasPendingSchedule) continue;

      processor._hasPendingSchedule = false;
      processor._executeBatch(frameStart);

      if (performance.now() - frameStart > this.frameBudgetMs) {
        // 帧预算用完，剩余的推到下一帧
        this._requestFlush();
        return;
      }
    }
  }

  /**
   * 取消所有 processor 的未完成任务。
   * 适用于：画布切换、标签页切换等场景。
   */
  cancelAll(): void {
    this._globalVersion++;
    this._cancelRaf();
    for (const processor of this._processors) {
      processor._hasPendingSchedule = false;
    }
  }

  /**
   * 销毁调度器。取消所有任务并清空 processor 注册表。
   * 适用于：画布彻底销毁。
   */
  destroy(): void {
    this.cancelAll();
    for (const processor of this._processors) {
      processor._scheduler = null;
    }
    this._processors.clear();
  }

  private _cancelRaf(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}

// ============================================================
// BatchProcessor - 单个分批任务
// ============================================================

export interface BatchProcessorOptions<T> {
  /** 获取待处理项列表（每批开头重新获取，确保列表是最新的） */
  getItems: () => T[];
  /** 对单个项的处理逻辑 */
  processItem: (item: T) => void;
  /** 同步处理阈值，项数 <= 此值时同步完成，默认 200 */
  syncThreshold?: number;
  /** 每批处理数量，默认 100 */
  batchSize?: number;
  /** 单个 processor 每帧时间预算(ms)，默认 4ms */
  timeBudgetMs?: number;
  /** 全部处理完成后的回调 */
  onComplete?: () => void;
}

export class BatchProcessor<T> {
  /** 实例级版本号，每次 run/schedule/cancel 递增 */
  private _localVersion = 0;
  /** 独立 rAF（未绑定 scheduler 时使用） */
  private _rafId: number | null = null;

  private readonly _getItems: () => T[];
  private readonly _processItem: (item: T) => void;
  private readonly _syncThreshold: number;
  private readonly _batchSize: number;
  private readonly _timeBudgetMs: number;
  private readonly _onComplete?: () => void;

  /** 分批进行中的偏移量（用于跨帧续传） */
  private _offset = 0;

  /** 调度器引用（可选，为 null 时降级为独立模式） */
  _scheduler: BatchScheduler | null;
  /** 是否有待执行的 schedule（调度器读取） */
  _hasPendingSchedule = false;

  constructor(options: BatchProcessorOptions<T>, scheduler?: BatchScheduler) {
    this._getItems = options.getItems;
    this._processItem = options.processItem;
    this._syncThreshold = options.syncThreshold ?? 200;
    this._batchSize = options.batchSize ?? 100;
    this._timeBudgetMs = options.timeBudgetMs ?? 4;
    this._onComplete = options.onComplete;
    this._scheduler = scheduler ?? null;
  }

  /**
   * 立即执行一轮处理。
   * 自动取消上一轮未完成的分批任务。
   */
  run(): void {
    this._localVersion++;
    this._hasPendingSchedule = false;
    this._cancelRaf();
    this._offset = 0;
    this._execute(this._localVersion);
  }

  /**
   * 合并到下一帧执行。
   * 绑定了 scheduler 时走统一 rAF，否则独立 rAF。
   */
  schedule(): void {
    this._localVersion++;
    this._offset = 0;

    if (this._scheduler) {
      this._hasPendingSchedule = true;
      this._scheduler._requestFlush();
    } else {
      this._scheduleStandalone();
    }
  }

  /** 取消未完成的任务 */
  cancel(): void {
    this._localVersion++;
    this._hasPendingSchedule = false;
    this._offset = 0;
    this._cancelRaf();
  }

  /** 从调度器中移除并取消所有任务 */
  destroy(): void {
    this.cancel();
    this._scheduler?._unregister(this);
    this._scheduler = null;
  }

  /** 当前是否有待执行的任务 */
  get pending(): boolean {
    return this._hasPendingSchedule || this._rafId !== null;
  }

  /** 独立模式的 schedule（无 scheduler 时降级） */
  private _scheduleStandalone(): void {
    if (this._rafId !== null) return;

    const localVer = this._localVersion;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (localVer !== this._localVersion) return;
      this._execute(localVer);
    });
  }

  /** 由 scheduler flush 调用 */
  _executeBatch(frameStart: number): void {
    this._execute(this._localVersion, frameStart);
  }

  private _execute(localVersion: number, frameStart?: number): void {
    const items = this._getItems();
    const total = items.length;

    // 少量对象直接同步处理
    if (total <= this._syncThreshold) {
      for (let i = 0; i < total; i++) {
        this._processItem(items[i]);
      }
      this._offset = 0;
      this._onComplete?.();
      return;
    }

    const batchSize = this._batchSize;
    const timeBudget = this._timeBudgetMs;
    const start = frameStart ?? performance.now();

    const currentItems = this._getItems();
    const len = currentItems.length;

    while (this._offset < len) {
      const end = Math.min(this._offset + batchSize, len);
      for (let i = this._offset; i < end; i++) {
        this._processItem(currentItems[i]);
      }
      this._offset = end;

      if (this._offset < len && performance.now() - start > timeBudget) {
        this._scheduleResume(localVersion);
        return;
      }
    }

    // 全部完成
    this._offset = 0;
    this._onComplete?.();
  }

  private _scheduleResume(localVersion: number): void {
    if (this._scheduler) {
      this._hasPendingSchedule = true;
      this._scheduler._requestFlush();
    } else {
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        if (localVersion !== this._localVersion) return;
        this._execute(localVersion);
      });
    }
  }

  private _cancelRaf(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }
}
